import { memo } from "react";
import type { IncidentFeature, FallbackTier } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

/**
 * Campus brief — left-rail content for KMITL (Lat Krabang, east Bangkok).
 *
 * KMITL sits on the Eastern transit spine: the Airport Rail Link (ARL Lat
 * Krabang station), the SRT Eastern line, BMTA feeder routes, and Motorway
 * No.7. It is ringed by a large private-dormitory belt (~196 dorms housing
 * 15-20k off-campus students). This panel surfaces the mobility + dorm
 * picture an operations lead actually watches:
 *
 *   1. Rail ridership feeding the campus (ARL + SRT Eastern)
 *   2. BMTA feeder-route load on the campus arterials
 *   3. Campus shuttle / tram circulation
 *   4. Dormitory-belt occupancy across the surrounding sois
 *   5. Live incidents on or near campus
 *
 * Numbers tagged "live" come from the API; everything else is MODELED from a
 * time-of-day profile and clearly labelled as such. Replace the modeled rows
 * with real sensor / operator feeds (ARL gate counts, BMTA AVL, shuttle GPS,
 * dorm occupancy) when those integrations land.
 */

interface Props {
  hour: number;
  isWeekend: boolean;
  iticEvents: IncidentFeature[];
  cityReports: IncidentFeature[];
  trafficSampleCount: number;
  fallbackTier?: FallbackTier;
  ageMinutes?: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Modeled mobility data — swap with real feeds when KMITL exposes them
// ────────────────────────────────────────────────────────────────────────────

// Time-of-day profile: idle overnight, two commute peaks, gentle midday floor.
function hourlyLoad(hour: number, isWeekend: boolean): number {
  const morningPeak = Math.exp(-((hour - 8) ** 2) / 1.8);
  const eveningPeak = Math.exp(-((hour - 17.5) ** 2) / 2.4);
  const overnight = hour >= 22 || hour < 5 ? 0.12 : 0.55;
  const weekendFactor = isWeekend ? 0.55 : 1;
  return Math.min(1.15, overnight + weekendFactor * 0.95 * Math.max(morningPeak, eveningPeak));
}

// Rail / bus feeders into campus. `base` is a relative weight; multiplied by the
// hourly commute profile to produce a modeled load index (not real ridership).
interface TransitLine {
  id: string;
  name: string;
  mode: string;
  base: number;
}
const TRANSIT_LINES: TransitLine[] = [
  { id: "arl",        name: "ARL · Lat Krabang",   mode: "rail", base: 0.78 },
  { id: "srt-east",   name: "SRT Eastern line",    mode: "rail", base: 0.55 },
  { id: "bmta-feeder", name: "BMTA feeder buses",  mode: "bus",  base: 0.62 },
  { id: "mtw-7",      name: "Motorway No.7",       mode: "road", base: 0.70 },
];

// Campus shuttle / tram circulation. Modeled headway-based "load" index.
interface ShuttleRoute {
  id: string;
  name: string;
  base: number;
}
const SHUTTLE_ROUTES: ShuttleRoute[] = [
  { id: "tram-a", name: "Tram Loop A · gate ↔ faculties", base: 0.60 },
  { id: "tram-b", name: "Tram Loop B · dorm belt ↔ core", base: 0.55 },
  { id: "evcart", name: "EV cart on-demand",              base: 0.40 },
];

// Dormitory belt around campus — ~196 private dorms, 15-20k off-campus students.
// Modeled occupancy curve: highest overnight + term-time, dips midday.
interface DormCluster {
  id: string;
  name: string;
  beds: number;
}
const DORM_CLUSTERS: DormCluster[] = [
  { id: "north", name: "Soi Chalong Krung N", beds: 6200 },
  { id: "east",  name: "Lat Krabang Rd E",    beds: 4800 },
  { id: "south", name: "Soi 1-Soi 17 belt",   beds: 5200 },
  { id: "west",  name: "On Nut–ARL approach",  beds: 2400 },
];

function dormOccupancy(cluster: DormCluster, hour: number, isWeekend: boolean): number {
  // Beds are fullest overnight (students sleeping) and emptier midday (on campus).
  const overnight = hour >= 22 || hour < 6 ? 0.93 : 0.55;
  const middayDip = Math.exp(-((hour - 13) ** 2) / 14) * 0.25;
  const weekendBoost = isWeekend ? 0.08 : 0;
  return Math.min(0.97, overnight - middayDip + weekendBoost);
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export const CampusBrief = memo(function CampusBrief({ hour, isWeekend, iticEvents, cityReports, trafficSampleCount, fallbackTier, ageMinutes }: Props) {
  const load = hourlyLoad(hour, isWeekend);

  const totalBeds = DORM_CLUSTERS.reduce((s, c) => s + c.beds, 0);
  const totalOccupied = DORM_CLUSTERS.reduce((s, c) => s + Math.round(c.beds * dormOccupancy(c, hour, isWeekend)), 0);
  const totalOccupancyPct = Math.round((totalOccupied / totalBeds) * 100);

  const openIncidents = cityReports.filter((r) => r.status !== "resolved").length + iticEvents.length;

  return (
    <div className="campus-brief-brief">
      <PanelHeader
        title="TRANSIT & DORMS"
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="itic·city-reports"
      />
      {/* ── Dorm-belt rollup ── */}
      <section className="campus-brief-section">
        <header className="campus-brief-h">
          <span className="eyebrow mono">Dormitory belt</span>
          <span className="mono caption">~ 196 dorms</span>
        </header>
        <div className="campus-brief-kv-grid">
          <div className="campus-brief-kv">
            <div className="num">{(totalBeds / 1000).toFixed(1)}k</div>
            <div className="lbl">BEDS · MODELED</div>
          </div>
          <div className="campus-brief-kv">
            <div className="num">{openIncidents}</div>
            <div className="lbl">OPEN INCIDENTS</div>
          </div>
        </div>
      </section>

      {/* ── Rail + bus feeders ── */}
      <section className="campus-brief-section">
        <header className="campus-brief-h">
          <span className="eyebrow mono">Feeder load</span>
          <span className="mono caption">hour {String(hour).padStart(2, "0")}{isWeekend ? " · weekend" : ""}</span>
        </header>
        <ul className="campus-brief-rows">
          {TRANSIT_LINES.map((t) => {
            const pct = Math.min(1, t.base * load * 1.3);
            const colour =
              pct > 0.8 ? "var(--bad)" : pct > 0.6 ? "var(--warn)" : pct > 0.4 ? "var(--data)" : "var(--good)";
            return (
              <li key={t.id} className="campus-brief-row">
                <span className="campus-brief-row-name" title={t.mode}>{t.name}</span>
                <span className="campus-brief-row-bar">
                  <span className="campus-brief-row-fill" style={{ width: `${Math.round(pct * 100)}%`, background: colour }} />
                </span>
                <span className="campus-brief-row-val mono">{Math.round(pct * 100)}%</span>
              </li>
            );
          })}
        </ul>
        <div className="campus-brief-foot mono">
          {trafficSampleCount} road samples · modeled · iTIC live: {iticEvents.length}
        </div>
      </section>

      {/* ── Campus shuttle / tram ── */}
      <section className="campus-brief-section">
        <header className="campus-brief-h">
          <span className="eyebrow mono">Campus shuttle / tram</span>
          <span className="mono caption">{SHUTTLE_ROUTES.length} routes</span>
        </header>
        <ul className="campus-brief-rows">
          {SHUTTLE_ROUTES.map((r) => {
            const pct = Math.min(1, r.base * load * 1.4);
            const colour = pct > 0.85 ? "var(--bad)" : pct > 0.65 ? "var(--warn)" : "var(--good)";
            return (
              <li key={r.id} className="campus-brief-row">
                <span className="campus-brief-row-name">{r.name}</span>
                <span className="campus-brief-row-bar">
                  <span className="campus-brief-row-fill" style={{ width: `${Math.round(pct * 100)}%`, background: colour }} />
                </span>
                <span className="campus-brief-row-val mono">{Math.round(pct * 100)}%</span>
              </li>
            );
          })}
        </ul>
        <div className="campus-brief-foot mono">modeled · GPS feed pending shuttle integration</div>
      </section>

      {/* ── Dorm occupancy by cluster ── */}
      <section className="campus-brief-section">
        <header className="campus-brief-h">
          <span className="eyebrow mono">Dorm occupancy</span>
          <span className="mono caption">{(totalOccupied / 1000).toFixed(1)}k/{(totalBeds / 1000).toFixed(1)}k · {totalOccupancyPct}%</span>
        </header>
        <ul className="campus-brief-rows">
          {DORM_CLUSTERS.map((c) => {
            const occ = dormOccupancy(c, hour, isWeekend);
            const filled = Math.round(c.beds * occ);
            const colour = occ > 0.9 ? "var(--warn)" : "var(--good)";
            return (
              <li key={c.id} className="campus-brief-row">
                <span className="campus-brief-row-name" title={c.name}>{c.name}</span>
                <span className="campus-brief-row-bar">
                  <span className="campus-brief-row-fill" style={{ width: `${Math.round(occ * 100)}%`, background: colour }} />
                </span>
                <span className="campus-brief-row-val mono">{filled}/{c.beds}</span>
              </li>
            );
          })}
        </ul>
        <div className="campus-brief-foot mono">modeled · sensor feed pending dorm-belt integration</div>
      </section>
    </div>
  );
});
